import { Component, ElementRef, OnInit, QueryList, ViewChildren, WritableSignal, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { take, tap } from 'rxjs';
import { filter, shareReplay, skipWhile } from 'rxjs/operators';

import { InputType } from '@core/enums/input-type';
import { Role } from '@core/enums/roles';

import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { UserManagementService } from '../management/services/user-management.service';

import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { IUser } from '@core/interfaces/iuser';
import { IDistrict, IGrades } from '@core/interfaces/ilookup';
import { ISchool } from '../management/interfaces/isearchSchool';

import { BannerService } from '@core/services/banner.service'; // keep for saving
import { IBanner } from '@core/interfaces/ibanner';
import { ToastService } from '@core/services/toast.service';
import { UserSettingsTabs } from '../management/enums/tabs';
import { BannerSocketService } from '@core/services/socket.service';
import { CustomValidators } from '@core/validators/field.validator';

@Component({
  selector: 'prism-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss']
})
export class AccountSettingsComponent implements OnInit {
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private userService = inject(UserManagementService);
  private lookupService = inject(LookupService);
  private bannerService = inject(BannerService);
  private bannerSocket = inject(BannerSocketService);
  private toastService = inject(ToastService);

  currentUser: WritableSignal<ILoginResponse | null> = this.authService.currentUserInfo;
  userInfo?: ILoginResponse | null;
  currentDistrict: IDistrict | null = this.authService.getCurrentDistrict() || null;

  isEditable = false;
  isSuperAdmin = false;
  isDistrictAdmin = false;
  isUser = false;

  settingsForm!: FormGroup;

  nonWhitespace(control: AbstractControl): ValidationErrors | null {
    const v = (control.value ?? '').toString();
    return /\S/.test(v) ? null : { requiredNonWhitespace: true };
  }

  bannerTableForm = this.fb.group({
    message: ['', [Validators.required, this.nonWhitespace, Validators.maxLength(500)]],
    isActive: false
  });

  banner$ = this.bannerSocket.banner$.pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  schoolOptions: ISchool[] = [];
  arrDistrictList: number[] = [];
  arrGradeList: number[] = [];

  inputType = InputType;
  roles = Role;
  tabs = [UserSettingsTabs.Profile, UserSettingsTabs.Site];
  selectedIndex = 0;

  @ViewChildren('tabHeader') private tabHeaders!: QueryList<ElementRef<HTMLElement>>;

  get firstName() { return this.settingsForm.get('firstName'); }
  get lastName() { return this.settingsForm.get('lastName'); }
  get districtIds() { return this.settingsForm.get('districtIds'); }
  get schoolId() { return this.settingsForm.get('schoolId'); }
  get emailName() { return this.settingsForm.get('emailName'); }
  get gradeIds() { return this.settingsForm.get('gradeIds'); }
  get messageCtrl() { return this.bannerTableForm.get('message'); }

  ngOnInit(): void {
    const user = this.currentUser()!;
    this.userInfo = { ...user };
    const roleId = this.userInfo?.roleId;
    this.isEditable = roleId !== Role.User;
    this.isSuperAdmin = roleId === Role.SystemAdmin;
    this.isDistrictAdmin = roleId === Role.DistrictAdmin;
    this.isUser = roleId === Role.User;

     this.tabs = this.userInfo?.roleId === Role.SystemAdmin
  ? [UserSettingsTabs.Profile, UserSettingsTabs.Site] as const
  : [UserSettingsTabs.Profile] as const;


    this.initProfileForm();
    this.updateProfileForm();
    this.listenOrganization();
    this.hydrateBannerForm();
  }

  private initProfileForm(): void {
    this.settingsForm = this.fb.group({
      firstName: [null, [Validators.required, Validators.maxLength(50)]],
      lastName: [null, [Validators.required, Validators.maxLength(50)]],
      districtIds: [[]],
      schoolId: [null],
      emailName: [null, [Validators.required, Validators.email, CustomValidators.noWhitespace(), CustomValidators.blacklistEmail()]],
      gradeIds: [[]]
    });
  }

  private updateProfileForm(): void {
    const user = this.currentUser();

    this.settingsForm.patchValue({
      ...user,
      districtIds: this.isUser ? this.currentDistrict?.id ?? null : null,
      schoolId: this.isUser ? user?.schoolId ?? null : null
    }, { emitEvent: false });

    this.lookupService.getOrganizations()
      .pipe(take(1))
      .subscribe((res: IDistrict[]) => {
        this.arrDistrictList = res.map(o => o.id);

        if (this.isUser) {
          const userDistrictId = this.currentDistrict?.id ?? this.arrDistrictList[0] ?? null;
          this.settingsForm.patchValue({ districtIds: userDistrictId }, { emitEvent: false });
          this.getSchoolsByDistrict(userDistrictId as number, user?.schoolId ?? undefined);
        } else {
          this.settingsForm.patchValue({
            districtIds: this.arrDistrictList?.length ? this.arrDistrictList : []
          }, { emitEvent: false });
        }

        if (user?.roleId) this.applyRoleValidation(user.roleId);
      });

    this.lookupService.getGrades()
      .pipe(take(1))
      .subscribe((grades: IGrades[]) => {
        this.arrGradeList = grades.map(g => g.id);
        this.settingsForm.patchValue({ gradeIds: this.arrGradeList }, { emitEvent: false });
        if (user?.roleId) this.applyRoleValidation(user.roleId);
      });
  }

  private applyRoleValidation(roleId: number): void {
    if (roleId === Role.SystemAdmin) {
      this.districtIds?.clearValidators();
      this.schoolId?.clearValidators();
      this.gradeIds?.clearValidators();
      this.districtIds?.setValue([]);
      this.gradeIds?.setValue([]);
      this.schoolId?.setValue(null);
    } else {
      this.districtIds?.setValidators([Validators.required]);

      if (roleId === Role.User) {
        this.schoolId?.setValidators([Validators.required]);

        const currentDistrictIds = this.districtIds?.value;
        if (Array.isArray(currentDistrictIds) && currentDistrictIds.length > 1) {
          this.districtIds?.setValue([currentDistrictIds[0]]);
        }
      } else {
        this.schoolId?.clearValidators();
        this.schoolId?.setValue(null);
        this.gradeIds?.clearValidators();
        this.gradeIds?.setValue([]);
      }
    }

    this.districtIds?.updateValueAndValidity();
    this.schoolId?.updateValueAndValidity();
    this.gradeIds?.updateValueAndValidity();
  }

  private listenOrganization(): void {
    this.districtIds?.valueChanges.subscribe((districtValue: number | number[]) => {
      if (this.userInfo?.roleId === Role.User) {
        const districtId = typeof districtValue === 'number' ? districtValue : districtValue?.[0];
        if (districtId) {
          if (this.schoolOptions.length === 0 || !this.schoolOptions.some(s => s.districtId === districtId)) {
            this.schoolId?.setValue(null);
            this.getSchoolsByDistrict(districtId);
          }
        } else {
          this.schoolId?.setValue(null);
          this.schoolOptions = [];
        }
      }
    });

    this.districtIds?.valueChanges.subscribe(() => {
      const c = this.districtIds;
      if (c && c.hasError('required')) {
        const v = c.value;
        if (v !== null && v !== '' && (Array.isArray(v) ? v.length > 0 : true)) {
          c.setErrors(null);
        }
      }
    });
  }

  private getSchoolsByDistrict(districtId: number, preserveSchoolId?: number): void {
    if (!districtId) return;
    this.lookupService.getSchoolsByDistrict(districtId)
      .pipe(take(1))
      .subscribe(schools => {
        this.schoolOptions = schools;
        if (preserveSchoolId && schools.some(s => s.id === preserveSchoolId)) {
          this.schoolId?.setValue(preserveSchoolId);
        }
      });
  }

  private hydrateBannerForm(): void {
  this.banner$
    .pipe(
      // Skip the service's default { message: '', isActive: false }
      // Wait until we see a meaningful payload from the server/socket
      skipWhile(b => !b || (!b.isActive && (b.message ?? '').trim() === '')),
      take(1)
    )
    .subscribe(banner => {
      // Reset avoids partial/Firefox-restored values
      this.bannerTableForm.reset(
        {
          message: banner.message ?? '',
          isActive: !!banner.isActive
        },
        { emitEvent: false }
      );

      // Nudge Firefox in case it restored fields after hard refresh
      queueMicrotask(() => {
        this.bannerTableForm.get('message')?.setValue(banner.message ?? '', { emitEvent: false });
        this.bannerTableForm.get('isActive')?.setValue(!!banner.isActive, { emitEvent: false });
      });

      this.bannerTableForm.markAsPristine();
    });
  }


  saveChanges(): void {
    const payload: IUser = { ...this.settingsForm.value };
    payload.id = this.userInfo?.userId!;
    payload.roleId = this.userInfo?.roleId!;
    payload.districtIds = this.isUser && this.currentDistrict?.id
      ? [this.currentDistrict.id]
      : payload.districtIds;

    this.userService.editUser(payload, 'Updated profile successfully')
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.currentUser.update(value => {
            const update: any = { ...value, ...payload };
            update.fullName = `${payload.firstName} ${payload.lastName}`;
            localStorage.setItem('prism-user', JSON.stringify(update));
            return update;
          });
        }
      });
  }

  setSelected(i: number) {
    if (i < 0 || i >= this.tabs.length) return;
    this.selectedIndex = i;
    queueMicrotask(() => this.focusTab(i));
  }

  private focusTab(i: number) {
    const el = this.tabHeaders?.get(i)?.nativeElement;
    el?.focus();
  }

  onHeaderKey(event: KeyboardEvent, i: number) {
    const last = this.tabs.length - 1;
    switch (event.key) {
      case 'ArrowRight':
      case 'Right':
        this.setSelected(i === last ? 0 : i + 1);
        event.preventDefault(); return;
      case 'ArrowLeft':
      case 'Left':
        this.setSelected(i === 0 ? last : i - 1);
        event.preventDefault(); return;
      case 'Home':
        this.setSelected(0);
        event.preventDefault(); return;
      case 'End':
        this.setSelected(last);
        event.preventDefault(); return;
      case ' ':
      case 'Enter':
        this.setSelected(i);
        event.preventDefault(); return;
    }
  }

  saveBannerRow(): void {
    if (this.bannerTableForm.invalid) {
      this.bannerTableForm.markAllAsTouched();
      return;
    }

    const v = this.bannerTableForm.value;
    const payload: IBanner = {
      message: (v.message ?? '').trim(),
      isActive: !!v.isActive
    };

    this.bannerService.saveBanner(payload)
      .pipe(take(1), tap(() => {
        this.bannerTableForm.markAsPristine();
        this.toastService.success('Banner updated');
      }))
      .subscribe();
  }
}
